import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Modal, Button, Box } from '@mui/material';
import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import CC from '../pdf/CC.pdf';
import Rockwell from '../fonts/ROCK.TTF';
import RockwellBold from '../fonts/Rockwell-Bold.ttf';
import { buttonBackgroundColor } from '../common';

const CourseCompletion = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [studentName, setStudentName] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const formatDateWithOrdinal = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'long' }).toUpperCase();
    const year = date.getFullYear();
    const daySuffix = getOrdinalSuffix(day);

    return {
      day: day.toString(),
      suffix: daySuffix,
      month,
      year: year.toString(),
      fullDate: `${day}${daySuffix} ${month} ${year}`,
      parts: [day.toString(), daySuffix, month, year.toString()]
    };
  };

  const formatDateForPDF = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const calculateCompletionDate = (commencementDate, type) => {
    const commencement = new Date(commencementDate);
    let completion;

    if (type === 'diploma') {
      completion = new Date(commencement);
      completion.setFullYear(commencement.getFullYear() + 1);
      completion.setDate(completion.getDate() - 1);
    } else if (type === 'advance_diploma') {
      completion = new Date(commencement);
      completion.setMonth(commencement.getMonth() + 6);
      completion.setDate(completion.getDate() - 1);
    } else {
      completion = commencement;
    }

    return completion.toISOString().split('T')[0];
  };

  const handlePdfEdit = async (studentData) => {
    try {
      const existingPdfBytes = await fetch(CC).then((res) => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      pdfDoc.registerFontkit(fontkit);

      const rockwellFontBytes = await fetch(Rockwell).then((res) => res.arrayBuffer());
      const rockwellBoldFontBytes = await fetch(RockwellBold).then((res) => res.arrayBuffer());
      const rockwellFont = await pdfDoc.embedFont(rockwellFontBytes);
      const rockwellBoldFont = await pdfDoc.embedFont(rockwellBoldFontBytes);

      const studentName = (studentData.name ? studentData.name.trim() : 'Student Name').toUpperCase();
      const nric = studentData.nric || 'N/A';
      const course = (studentData.batch?.course || 'COURSE NAME').replace(/\s*\(1\)$/, '');
      const dob = studentData.dob ? formatDate(studentData.dob) : 'Date of Birth not available';
      const approver = (props.approver || '').toUpperCase();

      const commencementDate = formatDateWithOrdinal(studentData.scheduleDate[0]?.startDate || new Date());
      const completionDate = formatDateWithOrdinal(calculateCompletionDate(studentData.scheduleDate[0]?.startDate, studentData.type));
      const completDate = formatDateForPDF(calculateCompletionDate(studentData.scheduleDate[0]?.startDate, studentData.type));

      const page = pdfDoc.getPages()[0];
      const gender = studentData.gender.toLowerCase();
      let title = gender === 'male' ? 'Mr.' : gender === 'female' ? 'Ms.' : 'Mr./Ms.';
      let secondgender = gender === 'male' ? 'his' : 'her';
      const nricfin = "FIN  /  NRIC no";
      const dobtext = "Date of Birth";
      const firstParagraph = `This is to certify that ${title} ***${studentName}*** is a bonafide student of this Institute, successfully completed ***${course}*** course in our Institute ***Jurong Academy Pte Ltd***  for the academic year ***${commencementDate.fullDate}******to******${completionDate.fullDate}.***`;
      const secondParagraph = `According to our school record,  ${secondgender}  ***${dobtext}***  is  ***${dob}***,  and  ${secondgender}  ***  ${nricfin}***  is  ***${nric}***  as  per  the  admission register of the Institution.`;
      const fontSize = 14;
      const x = 70;
      let y = 470;
      const maxWidth = 454;

      const drawText = (text, font, xPos, yPos, size, options = {}) => {
        page.drawText(text, {
          x: xPos,
          y: yPos + (options.superscript ? size * 0.3 : 0),
          size: options.superscript ? size * 0.8 : size,
          font,
          color: rgb(0, 0, 0),
        });
        return font.widthOfTextAtSize(text, options.superscript ? size * 0.6 : size);
      };

      // const drawDateWithSuperscript = (dateObj, startX, yPos, isBold) => {
      //   let currentX = startX;
      //   const font = isBold ? rockwellBoldFont : rockwellFont;

      //   // Draw day number
      //   currentX += drawText(dateObj.day, font, currentX, yPos, fontSize);

      //   // Draw ordinal suffix (superscript)
      //   currentX += drawText(dateObj.suffix, font, currentX, yPos, fontSize, { superscript: true });

      //   // Draw month and year
      //   const remainingText = ` ${dateObj.month} ${dateObj.year}`;
      //   currentX += drawText(remainingText, font, currentX, yPos, fontSize);

      //   return currentX;
      // };

      const processParagraph = (paragraph, yPos, maxLines) => {
        // Step 1: Split bold segments using *** delimiter
        const boldSegments = paragraph.split(/(\*\*\*.*?\*\*\*)/).filter(Boolean);

        // Step 2: Build word objects with bold flag
        let words = [];
        boldSegments.forEach(segment => {
          const isBold = segment.startsWith('***');
          const cleanedText = isBold ? segment.replace(/\*\*\*/g, '') : segment;

          cleanedText.trim().split(/\s+/).forEach(word => {
            words.push({
              text: word,
              bold: isBold,
              isDate: /^\d{1,2}(st|nd|rd|th)$/i.test(word)
            });
          });
        });

        const spaceWidth = rockwellFont.widthOfTextAtSize(' ', fontSize);
        let linesUsed = 0;

        while (words.length > 0 && linesUsed < maxLines) {
          let line = [];
          let lineWidth = 0;

          for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const font = word.bold ? rockwellBoldFont : rockwellFont;

            let wordWidth;
            if (word.isDate) {
              const day = word.text.match(/^(\d{1,2})/)[0];
              const suffix = word.text.match(/(st|nd|rd|th)$/i)[0];
              const dayWidth = font.widthOfTextAtSize(day, fontSize);
              const suffixWidth = font.widthOfTextAtSize(suffix, fontSize * 0.6);
              wordWidth = dayWidth + suffixWidth + 0;
            } else {
              wordWidth = font.widthOfTextAtSize(word.text, fontSize);
            }

            if (lineWidth + wordWidth + spaceWidth * line.length <= maxWidth) {
              line.push(word);
              lineWidth += wordWidth;
            } else {
              break;
            }
          }

          // Calculate spacing
          let cursorX = x;
          const spaceCount = line.length - 1;
          const totalWordWidth = line.reduce((acc, word) => {
            const font = word.bold ? rockwellBoldFont : rockwellFont;
            if (word.isDate) {
              const day = word.text.match(/^(\d{1,2})/)[0];
              const suffix = word.text.match(/(st|nd|rd|th)$/i)[0];
              const dayWidth = font.widthOfTextAtSize(day, fontSize);
              const suffixWidth = font.widthOfTextAtSize(suffix, fontSize * 0.6);
              return acc + dayWidth + suffixWidth;
            } else {
              return acc + font.widthOfTextAtSize(word.text, fontSize);
            }
          }, 0);

          const extraSpace = (spaceCount > 0 && words.length > line.length)
            ? (maxWidth - totalWordWidth) / spaceCount
            : spaceWidth;

          // Render line
          line.forEach((word, index) => {
            const font = word.bold ? rockwellBoldFont : rockwellFont;
            if (word.isDate) {
              const day = word.text.match(/^(\d{1,2})/)[0];
              const suffix = word.text.match(/(st|nd|rd|th)$/i)[0];
              cursorX += drawText(day, font, cursorX, yPos, fontSize);
              cursorX += drawText(suffix, font, cursorX, yPos, fontSize, { superscript: true });
            } else {
              cursorX += drawText(word.text, font, cursorX, yPos, fontSize);
            }

            if (index < line.length - 1) {
              cursorX += extraSpace;
            }
          });

          words.splice(0, line.length);
          yPos -= fontSize + 5;
          linesUsed++;
        }

        return yPos;
      };






      y = processParagraph(firstParagraph, y, 5);
      y -= fontSize + 15;
      y = processParagraph(secondParagraph, y, 3);

      // Draw footer information
      // const textWidth = rockwellFont.widthOfTextAtSize(localStorage.getItem('name'), 15);
      const rawName = localStorage.getItem('name');


      const name = rawName
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      const textWidth = rockwellFont.widthOfTextAtSize(name, 15);


      const pageWidth = 819;
      const centerX = (pageWidth - textWidth) / 2;


      page.drawText(name, {
        x: centerX,
        y: 182,
        size: 15,
        font: rockwellFont,
        color: rgb(0, 0, 0),
      });

      // page.drawText(`${approver}`, {
      //   x: 400,
      //   y: 150,
      //   size: 15,
      //   font: rockwellBoldFont,
      //   color: rgb(0, 0, 0),
      // });

      page.drawText(`${completDate}`, {
        x: 465,
        y: 606,
        size: 12,
        font: rockwellFont,
        color: rgb(0, 0, 0),
      });

      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      setPdfUrl(pdfUrl);
      setStudentName(studentData.name);
      handleOpen();
    } catch (error) {
      console.error('Error editing PDF:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return 'Invalid Date';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  useImperativeHandle(ref, () => ({
    handleDownload(student) {
      handlePdfEdit(student);
    },
  }));

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ width: '80%', height: '80%', margin: 'auto', marginTop: '5%', position: 'relative' }}>
        <iframe
          src={pdfUrl}
          style={{ width: '100%', height: '100%' }}
          title="Course Completion PDF"
        />
        <Box sx={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          display: 'flex',
          gap: 2
        }}>
          <Button
            variant="contained"
            style={{ backgroundColor: buttonBackgroundColor }}
            onClick={() => {
              const link = document.createElement('a');
              link.href = pdfUrl;
              link.download = `CourseCompletion_${studentName}.pdf`;
              link.click();
            }}
          >
            Download
          </Button>
          <Button variant="contained" color="error" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
});

export default CourseCompletion;